/**
 * Session / auth diagnostics for ZyMedElite, sent to MoEngage through trackZymedEvent.
 *
 * Goal: from one user's MoEngage timeline, see exactly why they were bounced to
 * "Re-login" or "maximum device limit" — which API rejected which token, whether a new
 * token was minted and the retry worked, what the app did next, and whether the logout
 * buttons actually worked.
 *
 * Tokens are never sent. Each token is described by non-reversible metadata only: a short
 * fingerprint (to tell "same token" from "new token" across events), issue/expiry times
 * and age.
 *
 * Every event must be fired BEFORE any storage wipe: user attributes are read from the
 * auth token in localStorage.
 */
import { jwtDecode } from "jwt-decode";
import { trackZymedEvent, ZYMED_EVENTS } from "./zymedAnalytics";

type Attrs = Record<string, unknown>;

/** FNV-1a 32-bit hash. Enough to correlate tokens across events; cannot recover the token. */
const fingerprint = (value: string): string => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
};

const unwrap = (raw: string | null | undefined): string | null => {
    if (!raw || raw === "null" || raw === "undefined") return null;
    // authToken is stored JSON-encoded (with quotes); the Pedia token is stored raw.
    if (raw.startsWith('"')) {
        try {
            return JSON.parse(raw);
        } catch {
            return raw;
        }
    }
    return raw;
};

/**
 * Describes a token without exposing it. `prefix` namespaces the keys so one event can
 * carry two tokens (e.g. `token_*` and `new_token_*`).
 */
export const tokenMeta = (rawToken: string | null | undefined, prefix = "token"): Attrs => {
    const token = unwrap(rawToken);
    if (!token) return { [`${prefix}_state`]: "missing" };
    try {
        const { iat, exp } = jwtDecode<{ iat?: number; exp?: number }>(token);
        const now = Date.now();
        return {
            [`${prefix}_state`]: exp && exp * 1000 <= now ? "expired" : "valid_format",
            [`${prefix}_fp`]: fingerprint(token),
            [`${prefix}_issued_at`]: iat ? new Date(iat * 1000).toISOString() : undefined,
            [`${prefix}_expires_at`]: exp ? new Date(exp * 1000).toISOString() : undefined,
            [`${prefix}_age_days`]: iat ? Math.round(((now - iat * 1000) / 86400000) * 10) / 10 : undefined,
            [`${prefix}_expires_in_hours`]: exp ? Math.round((exp * 1000 - now) / 3600000) : undefined,
        };
    } catch {
        return { [`${prefix}_state`]: "malformed", [`${prefix}_fp`]: fingerprint(token) };
    }
};

/** Path of an API URL without host or query string, e.g. "/content-management/content-detail". */
export const apiPath = (url?: string): string | undefined => {
    if (!url) return undefined;
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return url.split("?")[0];
    }
};

const truncate = (value: unknown, max = 120): string | undefined =>
    typeof value === "string" && value ? value.slice(0, max) : undefined;

export const deviceMeta = (): Attrs => ({
    device_type: localStorage.getItem("device_type") || "missing",
    device_info: localStorage.getItem("device_info") || "missing",
});

const pageMeta = (): Attrs => ({ page_path: window.location.pathname });

// The same rejection often hits several parallel calls; report each API once per window.
const THROTTLE_MS = 10_000;
const lastFired = new Map<string, number>();
const throttled = (key: string): boolean => {
    const now = Date.now();
    const last = lastFired.get(key);
    if (last && now - last < THROTTLE_MS) return true;
    lastFired.set(key, now);
    return false;
};

/** Which Pedia token mint path ran and how it ended. */
export const trackPediaTokenMint = (attrs: {
    reason: "missing" | "expiring" | "retry_after_403";
    outcome: "success" | "failed";
    failStage?: "doctor_details" | "missing_b2c_or_mobile" | "token_api" | "no_access_token";
    httpStatus?: number;
    durationMs: number;
    oldToken?: string | null;
    newToken?: string | null;
}) =>
    trackZymedEvent(ZYMED_EVENTS.SESSION_PEDIA_TOKEN_MINTED, {
        reason: attrs.reason,
        outcome: attrs.outcome,
        fail_stage: attrs.failStage,
        http_status: attrs.httpStatus,
        duration_ms: attrs.durationMs,
        ...tokenMeta(attrs.oldToken, "old_token"),
        ...(attrs.newToken ? tokenMeta(attrs.newToken, "new_token") : {}),
        ...deviceMeta(),
        ...pageMeta(),
    });

/** An API rejected the request's token (HTTP 401/403, or HTTP 200 with status 403 in the body). */
export const trackApiAuthRejected = (attrs: {
    tokenKind: "pedia" | "auth";
    url?: string;
    httpStatus?: number;
    body?: any;
    token?: string | null;
    attempt: "first" | "retry";
}) => {
    const api = apiPath(attrs.url);
    if (throttled(`rejected:${api}:${attrs.attempt}`)) return;
    trackZymedEvent(ZYMED_EVENTS.SESSION_API_AUTH_REJECTED, {
        token_kind: attrs.tokenKind,
        api,
        http_status: attrs.httpStatus,
        body_status: attrs.body?.status !== undefined ? String(attrs.body.status) : undefined,
        server_message: truncate(attrs.body?.message) ?? truncate(attrs.body?.error),
        attempt: attrs.attempt,
        ...tokenMeta(attrs.token),
        ...deviceMeta(),
        ...pageMeta(),
    });
};

/** Outcome of the silent re-mint + retry after a Pedia 403. */
export const trackPediaAuthRetry = (attrs: {
    url?: string;
    outcome: "recovered" | "still_rejected" | "mint_failed";
    httpStatus?: number;
    newToken?: string | null;
}) => {
    const api = apiPath(attrs.url);
    if (throttled(`retry:${api}:${attrs.outcome}`)) return;
    trackZymedEvent(ZYMED_EVENTS.SESSION_PEDIA_AUTH_RETRY, {
        api,
        outcome: attrs.outcome,
        http_status: attrs.httpStatus,
        ...(attrs.newToken ? tokenMeta(attrs.newToken, "new_token") : {}),
        ...pageMeta(),
    });
};

/**
 * The app is about to wipe storage, show "Re-login" and send the user to /on-boarding.
 * `handler` names the code path so each trigger can be told apart.
 */
export const trackForcedRelogin = (attrs: {
    handler: string;
    url?: string;
    httpStatus?: number;
    body?: any;
}) => {
    if (throttled(`relogin:${attrs.handler}`)) return;
    trackZymedEvent(ZYMED_EVENTS.SESSION_FORCED_RELOGIN, {
        handler: attrs.handler,
        api: apiPath(attrs.url),
        http_status: attrs.httpStatus,
        body_status: attrs.body?.status !== undefined ? String(attrs.body.status) : undefined,
        server_message: truncate(attrs.body?.message),
        in_native_app: !!(window as any).ReactNativeWebView,
        ...tokenMeta(localStorage.getItem("authToken"), "auth_token"),
        ...tokenMeta(localStorage.getItem("tatvaPediaToken"), "pedia_token"),
        ...deviceMeta(),
        ...pageMeta(),
    });
};

const sessionTimes = (sessions: any[]): string[] =>
    sessions.map((s) => s?.loggedInAt).filter((t): t is string => typeof t === "string" && !!t).sort();

/** HomePs decided to send the user to /session-exceed. */
export const trackDeviceLimitShown = (attrs: {
    allowedCount: number;
    sessions: any[];
    currentDeviceType: string;
}) => {
    const sessions = attrs.sessions || [];
    const registered = sessions.some((s) => s?.deviceType === attrs.currentDeviceType);
    const sameDeviceInfo = sessions.filter(
        (s) => s?.deviceInfo && s.deviceInfo === localStorage.getItem("device_info")
    ).length;
    trackZymedEvent(ZYMED_EVENTS.SESSION_DEVICE_LIMIT_SHOWN, {
        allowed_count: attrs.allowedCount,
        total_sessions: sessions.length,
        other_sessions: sessions.filter((s) => s?.deviceType !== attrs.currentDeviceType).length,
        current_device_registered: registered,
        // Sessions reporting the same browser/OS string as this device: a high count means
        // the same physical device was registered several times under different ids.
        sessions_with_same_device_info: sameDeviceInfo,
        oldest_session_at: sessionTimes(sessions)[0],
        newest_session_at: sessionTimes(sessions).slice(-1)[0],
        ...tokenMeta(localStorage.getItem("tatvaPediaToken"), "pedia_token"),
        ...deviceMeta(),
    });
};

/** The user tapped Logout on a device card on the device-limit screen. */
export const trackDeviceLogoutClicked = (device: any, listedCount: number) =>
    trackZymedEvent(ZYMED_EVENTS.SESSION_DEVICE_LOGOUT_CLICKED, {
        target_session_id: device?.id !== undefined ? String(device.id) : undefined,
        target_logged_in_at: device?.loggedInAt,
        target_device_info: device?.deviceInfo,
        target_is_same_device_info: !!device?.deviceInfo && device.deviceInfo === localStorage.getItem("device_info"),
        listed_devices: listedCount,
        ...deviceMeta(),
    });

/** Result of that device removal call. */
export const trackDeviceLogoutResult = (attrs: {
    targetSessionId?: unknown;
    outcome: "success" | "failed";
    httpStatus?: number;
    serverMessage?: string;
}) =>
    trackZymedEvent(ZYMED_EVENTS.SESSION_DEVICE_LOGOUT_RESULT, {
        target_session_id: attrs.targetSessionId !== undefined ? String(attrs.targetSessionId) : undefined,
        outcome: attrs.outcome,
        http_status: attrs.httpStatus,
        server_message: truncate(attrs.serverMessage),
    });

/** Result of the account-screen logout (Pedia session end + native logout). */
export const trackAccountLogoutResult = (attrs: {
    pediaLogout: "success" | "failed" | "skipped_no_token";
    serverMessage?: string;
    tokenBefore?: string | null;
}) =>
    trackZymedEvent(ZYMED_EVENTS.SESSION_ACCOUNT_LOGOUT_RESULT, {
        pedia_logout: attrs.pediaLogout,
        server_message: truncate(attrs.serverMessage),
        native_notified: !!(window as any).ReactNativeWebView,
        ...tokenMeta(attrs.tokenBefore, "pedia_token"),
        ...deviceMeta(),
    });

/** UseDeviceInfo minted a new device id. While logged in, that means storage was wiped. */
export const trackDeviceIdCreated = (attrs: { previousDeviceInfo: string | null; newDeviceType: string }) =>
    trackZymedEvent(ZYMED_EVENTS.SESSION_DEVICE_ID_CREATED, {
        new_device_type: attrs.newDeviceType,
        had_device_info: !!attrs.previousDeviceInfo,
        logged_in: !!localStorage.getItem("authToken"),
        had_b2c_id: !!localStorage.getItem("b2c_id"),
        ...pageMeta(),
    });
